import type { DeepInclude, WhereFilter } from "@types_def/common/filter.types";
import type { Delivery } from "@types_def/models/delivery.types";

import { DeliveryStatus } from "@types_def/models/delivery.types";
import { getDeliveryAttributesByStatus } from "@utils/functions/Delivery/DeliveryStatus/delivery-status-attributes";
import { addDays } from "date-fns";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserContext } from "@/contexts/UserContext/index.context";
import type { QueryType } from "@/types/common/query.types";
import { UserRole } from "@/types/models/user.type";

type TableState = {
  filter: WhereFilter<Delivery>;
  include: DeepInclude<Delivery>;
  deliveriesTableQueryFields: QueryType<Delivery>;
};

// Stable date reference for Alert filter - computed once
const alertDeadlineDate = addDays(new Date(), 2).toISOString();

export function useTableState(statusKey: DeliveryStatus, urlRole: "admin" | "client") {
  const { userState } = useUserContext();
  const userRole = userState?.["custom:role"] as UserRole;
  const organizationId = userState?.organizationId;
  const [searchParams] = useSearchParams();

  const idSearchParam = searchParams.get("deliveryId");
  const vinSearchParam = searchParams.get("vin");
  const plateSearchParam = searchParams.get("vehicleCode");
  const deliveryCountries = searchParams.get("deliveryCountries");
  const deliveryType = searchParams.get("deliveryType");
  const deliveryServices = searchParams.get("deliveryServices");
  const carDeliveryDeadlineFrom = searchParams.get("carDeliveryDeadlineFrom");
  const carDeliveryDeadlineTo = searchParams.get("carDeliveryDeadlineTo");
  const organizationSlugParam = searchParams.get("organizationSlug");

  const computedState = useMemo((): TableState => {
    const statusConfig = (
      getDeliveryAttributesByStatus as (s: DeliveryStatus) => {
        adminTableStatusFilter: DeliveryStatus[];
        clientTableStatusFilter: DeliveryStatus[];
        adminDeliveryInclude: DeepInclude<Delivery>;
        clientDeliveryInclude: DeepInclude<Delivery>;
        adminDeliveriesTableQueryFields: QueryType<Delivery>;
        clientDeliveriesTableQueryFields: QueryType<Delivery>;
      }
    )(statusKey);

    const baseFilter: WhereFilter<Delivery> =
      statusKey === DeliveryStatus.ALERT
        ? ({
            carDeliveryDeadline: { lte: alertDeadlineDate },
            offers: {},
          } as unknown as WhereFilter<Delivery>)
        : {};

    const filter: WhereFilter<Delivery> = {
      ...baseFilter,
      status: {
        in:
          urlRole === "admin"
            ? statusConfig.adminTableStatusFilter
            : statusConfig.clientTableStatusFilter,
      },
    };

    const organizationValue = userRole === UserRole.ADMIN ? organizationSlugParam : organizationId;
    if (organizationValue !== undefined && organizationValue !== null && organizationValue !== "") {
      filter.organization = { slug: { in: organizationValue.split(",") } };
    } else if (userRole === UserRole.CLIENT) {
      throw new Error("No Organization found for client account");
    }

    if (idSearchParam !== null && idSearchParam !== "") {
      const parsedId = Number.parseInt(idSearchParam, 10);
      if (!Number.isNaN(parsedId)) filter.id = parsedId;
    }

    const vehicleFilters: Record<string, unknown> = {};
    if (vinSearchParam !== null && vinSearchParam !== "") {
      vehicleFilters.vin = { contains: vinSearchParam };
    }
    if (plateSearchParam !== null && plateSearchParam !== "") {
      vehicleFilters.vehicleCode = { contains: plateSearchParam };
    }
    if (Object.keys(vehicleFilters).length > 0) {
      filter.vehicles = { some: vehicleFilters };
    }

    if (deliveryType !== null && deliveryType !== "") {
      filter.type = { in: deliveryType.split(",") };
    }

    const deadlineFilter: Record<string, unknown> = {};
    if (carDeliveryDeadlineFrom !== null && carDeliveryDeadlineFrom !== "") {
      const ts = Number.parseInt(carDeliveryDeadlineFrom, 10);
      if (!Number.isNaN(ts)) deadlineFilter.gte = new Date(ts).toISOString();
    }
    if (carDeliveryDeadlineTo !== null && carDeliveryDeadlineTo !== "") {
      const ts = Number.parseInt(carDeliveryDeadlineTo, 10);
      if (!Number.isNaN(ts)) deadlineFilter.lte = new Date(ts).toISOString();
    }
    if (Object.keys(deadlineFilter).length > 0) {
      // eslint-disable-next-line ts/no-unsafe-assignment
      filter.carDeliveryDeadline =
        deadlineFilter as unknown as WhereFilter<Delivery>["carDeliveryDeadline"];
    }

    if (deliveryServices !== null && deliveryServices !== "") {
      filter.optionIdFilter = deliveryServices;
    }

    if (deliveryCountries !== null && deliveryCountries !== "") {
      const countries = deliveryCountries.split(",");
      filter.OR = [
        { fromAddress: { country: { in: countries } } },
        { toAddress: { country: { in: countries } } },
        { toReturnFromAddress: { country: { in: countries } } },
        { toReturnToAddress: { country: { in: countries } } },
      ];
    }

    return {
      filter: Object.keys(filter).length > 0 ? filter : {},
      include:
        urlRole === "admin"
          ? statusConfig.adminDeliveryInclude
          : statusConfig.clientDeliveryInclude,
      deliveriesTableQueryFields:
        urlRole === "admin"
          ? statusConfig.adminDeliveriesTableQueryFields
          : statusConfig.clientDeliveriesTableQueryFields,
    };
  }, [
    statusKey,
    urlRole,
    userRole,
    organizationId,
    organizationSlugParam,
    idSearchParam,
    vinSearchParam,
    plateSearchParam,
    deliveryCountries,
    deliveryType,
    deliveryServices,
    carDeliveryDeadlineFrom,
    carDeliveryDeadlineTo,
  ]);

  return { tableState: computedState };
}
